import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { BASE_URL } from "../constants/global";
import { getImageUrl } from "../utils/imageUrls";
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { collectionApi } from '../utils/api';
import AuthenticatedImage from './AuthenticatedImage';

interface CollectionThumbnailsProps {
  collectionId: number;
  maxImages?: number;
}

const CollectionThumbnails: React.FC<CollectionThumbnailsProps> = ({ 
  collectionId, 
  maxImages = 3 
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { data: images = [], isLoading: loading } = useQuery({
    queryKey: ['collections', collectionId, 'thumbnails', maxImages],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const ids = await collectionApi.getImageIds(collectionId, token);
      const limitedIds = ids.slice(0, maxImages);
      if (limitedIds.length === 0) return [];
      return collectionApi.getImagesByIds(limitedIds, token);
    },
    enabled: isAuthenticated && !!collectionId,
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[...Array(maxImages)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
      {images.map(image => (
        <Box
          key={image.id}
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <AuthenticatedImage
            src={image.fileName ? getImageUrl(image.fileName, "thumbnail") : ""}
            alt={`Preview ${image.id}`}
            height={40}
            sx={{
              borderRadius: 1,
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

export default CollectionThumbnails;
